<script setup lang="ts">
import type { LimitExceedModalProps } from '@/types/index';
import { ref, watch } from 'vue';
import { isMobile } from '@basitcodeenv/vue3-device-detect';
const { visible, currentImage, loading } = defineProps<LimitExceedModalProps>();
const emit = defineEmits<{
    (e: 'redirectToSettings'): void;
    (e: 'update:visible', value: boolean): void;
}>();
const localVisible = ref(visible);

watch(
    () => visible,
    (newValue) => {
        localVisible.value = newValue;
    },
    { immediate: true }
);

watch(localVisible, (newValue) => {
    if (newValue !== visible) {
        emit('update:visible', newValue);
    }
});

function toCloseModal() {
    emit('update:visible', false);
}

function toRedirectToSettings() {
    emit('redirectToSettings');
    toCloseModal();
}
</script>
<template>
    <Dialog v-model:visible="localVisible" modal pt:root:class="!border-0 !bg-transparent" :class="{ 'p-dialog-maximized !max-w-full': isMobile, 'is-mobile': isMobile }" pt:mask:class="backdrop-blur-sm" style="width: 500px" @hide="toCloseModal">
        <template #container>
            <div class="limit-modal">
                <div class="limit-modal__image">
                    <img v-if="!loading && currentImage" :src="currentImage" alt="session-limit-exceed" />
                    <Skeleton v-else-if="loading" width="100%" height="200px" />
                </div>
                <div class="limit-modal__content">
                    <div class="limit-modal__text">
                        <h3 class="limit-modal__title">
                            <slot name="title"></slot>
                        </h3>
                        <div class="limit-modal__amount"><slot name="amount"></slot></div>
                        <p class="limit-modal__description">
                            <slot name="description"></slot>
                        </p>
                    </div>
                    <div class="limit-modal__buttons">
                        <slot name="buttons"></slot>
                    </div>
                    <div class="limit-modal__change-link">
                        <span>{{ $t('session_limit_change_text') }}</span>
                        <p @click="toRedirectToSettings">{{ $t('session_limit_change_link') }}</p>
                    </div>
                </div>
            </div>
        </template>
    </Dialog>
</template>

<style lang="scss" scoped>
.limit-modal {
    display: flex;
    flex-direction: column;
    border-radius: 20px;
    background-image: radial-gradient(circle at left top, var(--primary), var(--primary-light));
    overflow: hidden;
    color: var(--white);
    .is-mobile & {
        border-radius: 0px;
        height: 100%;
    }
    &__image {
        display: flex;
        width: 100%;
        height: 200px;
        img {
            width: 100%;
            height: 100%;
            object-fit: cover;
        }
        .is-mobile & {
            height: 300px;
        }
    }
    &__content {
        padding: 10px;
        display: flex;
        flex-direction: column;
        gap: 10px;
        .is-mobile & {
            flex: 1 1 auto;
            justify-content: center;
        }
        overflow: auto;
    }
    &__text {
    }

    &__title {
        font-size: 20px;
        font-weight: 700;
        text-align: center;
        display: flex;
        justify-content: center;
        align-items: center;
        flex-direction: column;
    }
    &__amount {
        font-size: 30px;
        width: 100%;
        text-align: center;
        font-weight: 700;
    }
    &__description {
    }

    &__buttons {
        display: flex;
        gap: 10px;
        align-items: center;
    }
    &__change-link {
        display: flex;
        flex-direction: column;
        justify-content: center;
        align-items: center;
        text-align: center;
        font-size: 12px;
        font-weight: 500;
        color: var(--white);
        p {
            cursor: pointer;
            color: var(--red);
            @media (any-hover: hover) {
                &:hover {
                    text-decoration: underline;
                }
            }
        }
    }
}
</style>
